import { Button } from "@/components/ui/button";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import { ClientCard } from "../components/Client/ClientCard";
import ClientFilter from "../components/Client/ClientFilter";
import ClientNavigation from "../components/Client/ClientNavigation";
import { useState, useCallback, useMemo, memo } from "react";
import { ClientListHeader } from "../components/Client/ClientListHeader";
import { ClientListRow } from "../components/Client/ClientListRow";
import { useGetClients, useDeactivateClient } from "../api/client.queries";
import { transformClientsForUI } from "../utils/clientUtils";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";

function ClientContainer() {
  const [view, setView] = useState("grid");
  const [selectedClients, setSelectedClients] = useState(new Set<string>());
  const [sortBy, setSortBy] = useState<string>("newest_first");
  const [filterPlan, setFilterPlan] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);

  const navigate = useNavigate();

  const { data: clientsResponse, isLoading, error } = useGetClients();
  const deactivateMutation = useDeactivateClient();

  // Transform API data for UI components
  const rawClients = useMemo(() => {
    if (!clientsResponse?.clients) return [];
    return transformClientsForUI(clientsResponse.clients);
  }, [clientsResponse]);

  // Get plan segments and counts (split comma-separated plans so filter options are readable)
  const planCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    rawClients.forEach((client) => {
      const plan = client.plan?.trim() || "";
      const segments = plan
        ? plan
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : ["Unknown"];
      segments.forEach((segment) => {
        counts[segment] = (counts[segment] || 0) + 1;
      });
    });
    return counts;
  }, [rawClients]);

  // Apply filtering, sorting, and search
  const uiClients = useMemo(() => {
    let filtered = [...rawClients];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (client) =>
          client.name.toLowerCase().includes(query) ||
          client.email.toLowerCase().includes(query) ||
          client.plan.toLowerCase().includes(query),
      );
    }

    // Apply plan filter (match if client's plan segments include the selected segment)
    if (filterPlan !== "all") {
      filtered = filtered.filter((client) => {
        const plan = client.plan?.trim() || "";
        const segments = plan
          ? plan
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
        return segments.includes(filterPlan);
      });
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "alphabetical_asc":
          return a.name.localeCompare(b.name);
        case "alphabetical_desc":
          return b.name.localeCompare(a.name);
        case "newest_first":
          return (
            new Date(b.dateCreated).getTime() -
            new Date(a.dateCreated).getTime()
          );
        case "oldest_first":
          return (
            new Date(a.dateCreated).getTime() -
            new Date(b.dateCreated).getTime()
          );
        default:
          return 0;
      }
    });

    return filtered;
  }, [rawClients, searchQuery, filterPlan, sortBy]);

  // Memoize client IDs to prevent recreation on every render
  const clientIds = useMemo(
    () => uiClients.map((client) => client.id),
    [uiClients],
  );

  const handleClientSelect = useCallback((clientId: string) => {
    setSelectedClients((prev) => {
      const newSelected = new Set(prev);
      if (newSelected.has(clientId)) {
        newSelected.delete(clientId);
      } else {
        newSelected.add(clientId);
      }
      return newSelected;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedClients((prev) =>
      prev.size === uiClients.length ? new Set() : new Set(clientIds),
    );
  }, [clientIds, uiClients.length]);

  // Memoize checkbox states for header to prevent unnecessary recalculations
  const checkboxStates = useMemo(() => {
    const allSelected =
      selectedClients.size === uiClients.length && uiClients.length > 0;
    const someSelected =
      selectedClients.size > 0 && selectedClients.size < uiClients.length;
    return { allSelected, someSelected };
  }, [selectedClients.size, uiClients.length]);

  const openDeactivateDialog = useCallback(() => {
    const ids = Array.from(selectedClients);
    if (ids.length === 0) {
      toast.error("Select at least one client to deactivate");
      return;
    }
    setDeactivateDialogOpen(true);
  }, [selectedClients]);

  const runDeactivate = useCallback(async () => {
    const ids = Array.from(selectedClients);
    for (const userId of ids) {
      await deactivateMutation.mutateAsync(userId);
    }
    setSelectedClients(new Set());
    toast.success(
      `Deactivated ${ids.length} client${ids.length === 1 ? "" : "s"} successfully`,
    );
  }, [selectedClients, deactivateMutation]);

  // Show loading state
  if (isLoading) {
    return (
      <main className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-7 overflow-y-auto px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
        <div className="flex flex-col gap-4">
          <div className="flex md:flex-col md:gap-4 lg:flex-row lg:items-center lg:justify-between">
            <AdminBreadcrumb title="Client Management" />
            <ClientNavigation
              view={view}
              setView={setView}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>
        </div>
        <div className="min-h-0 flex-1">
          <div className="scrollbar-thin grid min-h-0 auto-rows-max gap-4 pb-20">
            {/* Skeleton loaders for action buttons */}
            <div className="flex flex-row gap-2">
              <div className="h-[52px] w-[180px] animate-pulse rounded-lg bg-white/10" />
              <div className="h-[52px] w-[140px] animate-pulse rounded-lg bg-white/10" />
            </div>

            <div className="flex w-full justify-between gap-8 md:flex-col-reverse lg:flex-row">
              {/* Main content skeleton */}
              <div className="w-full">
                {view === "grid" ? (
                  /* Grid view skeleton */
                  <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {[...Array(8)].map((_, i) => (
                      <div
                        key={i}
                        className="h-[200px] animate-pulse rounded-lg bg-white/10"
                      />
                    ))}
                  </div>
                ) : (
                  /* List view skeleton */
                  <div className="flex flex-col gap-3">
                    {[...Array(6)].map((_, i) => (
                      <div
                        key={i}
                        className="h-[76px] animate-pulse rounded-lg bg-white/10"
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Filter sidebar skeleton */}
              <div className="flex h-fit w-full flex-col gap-4 md:w-full lg:w-[280px]">
                <div className="h-[120px] animate-pulse rounded-lg bg-white/10" />
                <div className="h-[200px] animate-pulse rounded-lg bg-white/10" />
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Show error state
  if (error) {
    return (
      <main className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-7 overflow-y-auto px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
        <div className="flex flex-col gap-4">
          <div className="flex md:flex-col md:gap-4 lg:flex-row lg:items-center lg:justify-between">
            <AdminBreadcrumb title="Client Management" />
            <ClientNavigation
              view={view}
              setView={setView}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          </div>
        </div>
        <div className="flex items-center justify-center p-8">
          <p className="text-red-500">
            Error loading clients. Please try again.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-7 overflow-y-auto px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
      {/* Fixed */}
      <div className="flex flex-col gap-4">
        <div className="flex md:flex-col md:gap-4 lg:flex-row lg:items-center lg:justify-between">
          <AdminBreadcrumb title="Client Management" />
          <ClientNavigation
            view={view}
            setView={setView}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
          />
        </div>
      </div>
      <div className="min-h-0 flex-1">
        <div className="scrollbar-thin grid min-h-0 auto-rows-max gap-4 pb-20">
          {view === "grid" && (
            <>
              {/* ACTION BUTTONS - Show above grid view */}
              <div className="flex flex-row gap-2">
                <Button
                  icon
                  variant="default"
                  className="h-[52px] w-auto p-3"
                  onClick={() => navigate({ to: "/admin/client/add" })}
                >
                  <i className="ri-add-line text-md font-light"></i>
                  Add new client
                </Button>
                <Button
                  icon
                  variant="default"
                  className="bg-black-950 hover:bg-black-950/80 flex h-[52px] w-auto flex-row gap-2 p-3"
                  onClick={openDeactivateDialog}
                  disabled={deactivateMutation.isPending}
                >
                  <i className="ri-delete-bin-line text-md font-light"></i>
                  Deactivate
                </Button>
              </div>

              <ConfirmDialog
                open={deactivateDialogOpen}
                onOpenChange={setDeactivateDialogOpen}
                title="Deactivate clients"
                description={`Deactivate ${selectedClients.size} client${selectedClients.size === 1 ? "" : "s"}? This action cannot be undone.`}
                confirmLabel="Deactivate"
                onConfirm={runDeactivate}
                isLoading={deactivateMutation.isPending}
              />

              <div className="flex w-full justify-between gap-8 md:flex-col-reverse lg:flex-row">
                {/* MAIN CONTENT AREA */}
                <div className="w-full">
                  {uiClients.length === 0 ? (
                    <div className="flex h-64 w-full items-center justify-center">
                      <div className="text-center">
                        <i className="ri-user-search-line text-black-500 mb-4 text-5xl"></i>
                        <p className="text-black-500 mb-2 text-lg font-medium">
                          No clients found
                        </p>
                        <p className="text-black-500 text-sm">
                          {searchQuery
                            ? "Try adjusting your search or filters"
                            : "No clients match the selected filters"}
                        </p>
                        {(searchQuery || filterPlan !== "all") && (
                          <Button
                            onClick={() => {
                              setSearchQuery("");
                              setFilterPlan("all");
                            }}
                            variant="default"
                            className="mt-4"
                          >
                            Clear filters
                          </Button>
                        )}
                      </div>
                    </div>
                  ) : (
                    /* GRID VIEW (existing) */
                    <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                      {uiClients.map((client) => (
                        <ClientCard
                          key={client.id}
                          clientId={client.id}
                          name={client.name}
                          email={client.email}
                          plan={client.plan}
                          subscriptionEndDate={client.subscriptionEndDate}
                          className="hover:scale cursor-pointer gap-5 transition-all duration-200 hover:scale-105"
                          showCheckbox
                          isSelected={selectedClients.has(client.id)}
                          onSelect={() => handleClientSelect(client.id)}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* FILTER SIDEBAR */}
                <ClientFilter
                  sortBy={sortBy}
                  onSortChange={setSortBy}
                  filterPlan={filterPlan}
                  onFilterChange={setFilterPlan}
                  planCounts={planCounts}
                  totalClients={rawClients.length}
                />
              </div>
            </>
          )}

          {view === "list" && (
            <div className="flex w-full justify-between gap-8 md:flex-col-reverse lg:flex-row">
              {/* ACTION BUTTONS - Show on left side for list view */}
              <div className="flex w-auto flex-row gap-2">
                <Button
                  icon
                  variant="default"
                  className="h-[52px] w-auto p-3"
                  onClick={() => navigate({ to: "/admin/client/add" })}
                >
                  <i className="ri-add-line text-md font-light"></i>
                  Add new client
                </Button>
                <Button
                  icon
                  variant="default"
                  className="bg-black-950 hover:bg-black-950/80 flex h-[52px] w-auto flex-row gap-2 p-3"
                  onClick={openDeactivateDialog}
                  disabled={deactivateMutation.isPending}
                >
                  <i className="ri-delete-bin-line text-md font-light"></i>
                  Deactivate
                </Button>
              </div>

              <ConfirmDialog
                open={deactivateDialogOpen}
                onOpenChange={setDeactivateDialogOpen}
                title="Deactivate clients"
                description={`Deactivate ${selectedClients.size} client${selectedClients.size === 1 ? "" : "s"}? This action cannot be undone.`}
                confirmLabel="Deactivate"
                onConfirm={runDeactivate}
                isLoading={deactivateMutation.isPending}
              />

              {/* MAIN CONTENT AREA */}
              <div className="w-full">
                {uiClients.length === 0 ? (
                  <div className="flex h-64 w-full items-center justify-center">
                    <div className="text-center">
                      <i className="ri-user-search-line text-black-500 mb-4 text-5xl"></i>
                      <p className="text-black-500 mb-2 text-lg font-medium">
                        No clients found
                      </p>
                      <p className="text-black-500 text-sm">
                        {searchQuery
                          ? "Try adjusting your search or filters"
                          : "No clients match the selected filters"}
                      </p>
                      {(searchQuery || filterPlan !== "all") && (
                        <Button
                          onClick={() => {
                            setSearchQuery("");
                            setFilterPlan("all");
                          }}
                          variant="default"
                          className="mt-4"
                        >
                          Clear filters
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  /* LIST VIEW */
                  <div className="w-full overflow-hidden rounded-lg">
                    <ClientListHeader
                      checked={checkboxStates.allSelected}
                      indeterminate={checkboxStates.someSelected}
                      onCheckedChange={handleSelectAll}
                    />
                    <div className="flex flex-col gap-3">
                      {uiClients.map((client) => (
                        <ClientListRow
                          key={client.id}
                          client={client}
                          isSelected={selectedClients.has(client.id)}
                          onSelect={() => handleClientSelect(client.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* FILTER SIDEBAR */}
              <ClientFilter
                sortBy={sortBy}
                onSortChange={setSortBy}
                filterPlan={filterPlan}
                onFilterChange={setFilterPlan}
                planCounts={planCounts}
                totalClients={rawClients.length}
              />
            </div>
          )}
        </div>
      </div>
    </main>
  );
}

export default memo(ClientContainer);
